/* =========================================================
   CUSTOM-CART-DRAWER.JS (STABLE)
   Fixes:
   ✅ Stops redirect to /cart by intercepting add-to-cart form submits
   ✅ Drawer opens after add (always)
   ✅ NO form cloning (avoids layout/CSS issues)
   ✅ No stacked listeners (single delegated handlers)
   ✅ Safe drawer re-render queue
   ✅ Meta ATC fires ONLY from our window.addToCart success
   ✅ InitiateCheckout fires ONLY on real checkout click
   ========================================================= */

let drawerLock = false;
let drawerUpdateInProgress = false;
let drawerQueuedUpdate = false;

/* ===========================
   META (Browser + CAPI mirror)
   =========================== */

const WORKER_URL = "https://boringluts-capi.ceo-452.workers.dev/";
const META_DEBUG = false;
const metaLog = (...a) => { if (META_DEBUG) console.log("[META]", ...a); };

function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[2]) : "";
  } catch (e) { return ""; }
}
function getFbp() { return getCookie("_fbp"); }
function getFbc() {
  const c = getCookie("_fbc");
  if (c) return c;
  try {
    const fbclid = new URL(location.href).searchParams.get("fbclid");
    return fbclid ? ("fb.1." + Date.now() + "." + fbclid) : "";
  } catch (e) { return ""; }
}

async function getCart() {
  const r = await fetch("/cart.js", { credentials: "same-origin" });
  if (!r.ok) throw new Error("Cart fetch failed");
  return r.json();
}

function getCurrencyFromCart(cart) {
  try { if (cart && cart.currency) return String(cart.currency); } catch (e) {}
  try { if (window.Shopify && Shopify.currency && Shopify.currency.active) return Shopify.currency.active; } catch (e) {}
  return "USD";
}

function buildContents(cart) {
  const items = (cart && cart.items) || [];
  return items.map(li => ({
    id: String(li.variant_id),
    quantity: Number(li.quantity || 1),
    item_price: Number((li.price || 0) / 100)
  }));
}

function cartTotal(cart) {
  try {
    if (cart && typeof cart.total_price === "number") {
      return Number((cart.total_price / 100).toFixed(2));
    }
  } catch (e) {}
  const items = (cart && cart.items) || [];
  return Number(
    items.reduce((sum, li) => sum + ((li.price || 0) / 100) * (li.quantity || 1), 0).toFixed(2)
  );
}

window.__metaFlags = window.__metaFlags || {};

// Fires ONLY when our addToCart succeeds
// ✅ CHANGE: accept ctx and use it for optional event_source_url
window.metaTrackAddToCart = async function ({ cart, variantId, item, ctx = {} }) {
  try {
    if (typeof window.fbq !== "function") return;

    const vId = String((item && (item.variant_id || item.id)) || variantId || "");
    if (!vId) return;

    // Dedup per-variant in a short window
    const flagKey = `atc_${vId}`;
    if (window.__metaFlags[flagKey]) return;
    window.__metaFlags[flagKey] = 1;
    setTimeout(() => { delete window.__metaFlags[flagKey]; }, 8000);

    const currency = getCurrencyFromCart(cart);

    const qty = Number(item?.quantity || 1);
    const lineCents = Number(item?.final_line_price ?? item?.line_price ?? 0);
    const value = Number((lineCents / 100).toFixed(2));
    const unitPrice = qty > 0 ? Number((value / qty).toFixed(2)) : 0;

    if (!value || value <= 0) return;

    const payload = {
      value,
      currency,
      content_ids: [vId],
      contents: [{ id: vId, quantity: qty, item_price: unitPrice }],
      content_type: "product",
      num_items: qty
    };

    const eventId = `atc-${vId}-${Date.now()}`;

    // ✅ CHANGE: optional event_source_url (used for upsell buttons)
    let eventSourceUrl = "";
    try {
      if (ctx && ctx.eventSourceUrl) {
        eventSourceUrl = String(ctx.eventSourceUrl);
        eventSourceUrl = eventSourceUrl.startsWith("http")
          ? eventSourceUrl
          : (location.origin + eventSourceUrl);
      }
    } catch (e) {}

    const fbqOptions = { eventID: eventId };
    if (eventSourceUrl) fbqOptions.event_source_url = eventSourceUrl;

    // Browser
    window.fbq("track", "AddToCart", payload, fbqOptions);

    // CAPI mirror (won't break UX if it fails)
    fetch(WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "AddToCart",
        event_id: eventId,
        user_data: { fbp: getFbp(), fbc: getFbc() },
        payload
      })
    }).catch(() => {});
  } catch (e) {
    metaLog("ATC error", e);
  }
};

window.trackInitiateCheckout = async function () {
  try {
    if (typeof window.fbq !== "function") return;

    const cart = await getCart();
    const token = cart?.token || String(Date.now());
    const flagKey = `ic_${token}`;
    if (sessionStorage.getItem(flagKey)) return;
    sessionStorage.setItem(flagKey, "1");
    setTimeout(() => sessionStorage.removeItem(flagKey), 12000);

    const payload = {
      value: cartTotal(cart),
      currency: getCurrencyFromCart(cart),
      contents: buildContents(cart),
      content_type: "product",
      num_items: (cart.items || []).reduce((n, li) => n + Number(li.quantity || 1), 0)
    };

    const eventId = `ic-${token}-${Date.now()}`;

    // Browser
    window.fbq("track", "InitiateCheckout", payload, { eventID: eventId });

    // CAPI mirror
    fetch(WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "InitiateCheckout",
        event_id: eventId,
        user_data: { fbp: getFbp(), fbc: getFbc() },
        payload
      })
    }).catch(() => {});
  } catch (e) {
    metaLog("IC error", e);
  }
};

/* ===========================
   HARD LOCK: prevent multi-add
   =========================== */

window.__blAtcLock = window.__blAtcLock || {};

/* ===========================
   ADD TO CART (single source)
   =========================== */

// ✅ CHANGE: accept 3rd param ctx and pass it through to metaTrackAddToCart
window.addToCart = async function (variantId, quantity = 1, ctx = {}) {
  const lockKey = `v_${variantId}`;

  // prevent rapid double calls
  if (window.__blAtcLock[lockKey]) return;
  window.__blAtcLock[lockKey] = 1;
  setTimeout(() => { delete window.__blAtcLock[lockKey]; }, 1200);

  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ id: variantId, quantity })
  });

  if (!res.ok) {
    console.error("Add to cart failed");
    return;
  }

  // Accurate line item for value/qty
  const item = await res.json();

  // Refresh cart + drawer UI
  const cart = await fetch("/cart.js").then(r => r.json());
  updateCartItemCounts(cart.item_count);

  // ✅ this will re-render AND open drawer (openAfterUpdate=true)
  await safeUpdateCartDrawer(true);

  // Fire ATC ONCE (only after success)
  try {
    await window.metaTrackAddToCart({ cart, variantId: String(variantId), item, ctx });
  } catch (e) {}

  window.dispatchEvent(new Event("scroll"));
};

/* ===========================
   STOP REDIRECTS TO /cart
   Intercept add-to-cart submits
   =========================== */

function isAddToCartForm(form) {
  if (!form || !(form instanceof HTMLFormElement)) return false;

  // Shopify themes usually use product-form form + data-type
  if (form.matches("product-form form, form[data-type='add-to-cart-form']")) return true;

  // fallback: action contains /cart/add
  const action = (form.getAttribute("action") || "").toLowerCase();
  if (action.includes("/cart/add")) return true;

  return false;
}

document.addEventListener("submit", async (e) => {
  const form = e.target;
  if (!isAddToCartForm(form)) return;

  // IMPORTANT: stop native POST redirect + stop other handlers stacking
  e.preventDefault();
  e.stopPropagation();
  if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

  const variantId =
    form.querySelector('[name="id"]')?.value ||
    form.querySelector('[name="variant"]')?.value;

  if (!variantId) return;

  await window.addToCart(variantId, 1);
}, true);

/* ===========================
   INIT
   =========================== */

document.addEventListener("DOMContentLoaded", () => {
  syncCartFromServer();

  // Clicking /cart should open drawer (not navigate)
  document.querySelectorAll('a[href="/cart"]').forEach((link) => {
    link.addEventListener("click", async (e) => {
      e.preventDefault();
      await safeUpdateCartDrawer(true);
      openCartDrawer();
      window.dispatchEvent(new Event("scroll"));
    });
  });
});

window.addEventListener("pageshow", syncCartFromServer);

// InitiateCheckout ONLY on real checkout click
document.addEventListener("click", (e) => {
  const el = e.target && e.target.closest(
    'a[href*="/checkout"], button[name="checkout"], button[id*="checkout"], input[name="checkout"]'
  );
  if (el) {
    try { window.trackInitiateCheckout(); } catch (err) {}
  }
}, true);

/* ===========================
   CART SYNC
   =========================== */

function syncCartFromServer() {
  fetch("/cart.js")
    .then((res) => res.json())
    .then((cart) => updateCartItemCounts(cart.item_count))
    .catch(() => {});
}

/* ===========================
   DRAWER OPEN/CLOSE
   =========================== */

function openCartDrawer() {
  const drawer = document.querySelector(".custom-cart-drawer");
  if (!drawer || drawer.classList.contains("custom-cart-drawer--active")) return;

  drawer.classList.add("custom-cart-drawer--active", "cart-drawer-animate-in");
  drawer.addEventListener(
    "animationend",
    () => drawer.classList.remove("cart-drawer-animate-in"),
    { once: true }
  );
}

function closeCartDrawer() {
  if (drawerLock) return;
  const drawer = document.querySelector(".custom-cart-drawer");
  if (!drawer) return;

  drawerLock = true;
  drawer.classList.add("cart-drawer-closing");

  setTimeout(() => {
    drawer.classList.remove(
      "custom-cart-drawer--active",
      "cart-drawer-closing",
      "cart-drawer-animate-in"
    );
    drawerLock = false;
  }, 300);
}

/* ===========================
   CART COUNT BUBBLES
   =========================== */

function updateCartItemCounts(count) {
  document.querySelectorAll("[data-cart-count]").forEach((bubble) => {
    let span = bubble.querySelector(".cart-count-number");

    if (count > 0 && count < 100) {
      if (!span) {
        span = document.createElement("span");
        span.className = "cart-count-number";
        span.setAttribute("aria-hidden", "true");
        bubble.prepend(span);
      }
      span.textContent = count;
      bubble.style.display = "inline-block";
    } else {
      if (span) span.remove();
      bubble.style.display = "none";
    }
  });
}

/* ===========================
   DRAWER UPDATE (re-render)
   =========================== */

async function updateCartDrawer(openAfterUpdate = false) {
  const drawer = document.querySelector(".custom-cart-drawer");
  if (!drawer) return;

  const box = drawer.querySelector(".custom-cart-drawer-box");
  if (!box) return;

  const wasOpen = drawer.classList.contains("custom-cart-drawer--active");
  drawer.classList.add("loading");

  const res = await fetch("/?section_id=custom-cart-drawer");
  const text = await res.text();
  const html = document.createElement("div");
  html.innerHTML = text;

  const newBox = html.querySelector(".custom-cart-drawer-box");
  if (newBox) {
    box.replaceWith(newBox);
    const shouldOpen = openAfterUpdate || wasOpen;
    if (shouldOpen) openCartDrawer();
  }

  drawer.classList.remove("loading");
}

async function safeUpdateCartDrawer(openAfterUpdate = false) {
  if (drawerUpdateInProgress) {
    drawerQueuedUpdate = true;
    return;
  }

  drawerUpdateInProgress = true;
  await updateCartDrawer(openAfterUpdate);
  drawerUpdateInProgress = false;

  if (drawerQueuedUpdate) {
    drawerQueuedUpdate = false;
    await safeUpdateCartDrawer(openAfterUpdate);
  }
}

/* ===========================
   DRAWER INTERACTIONS (delegated)
   =========================== */

document.addEventListener("click", async (e) => {
  const drawer = document.querySelector(".custom-cart-drawer");
  if (!drawer) return;

  // Close buttons
  if (e.target.closest(".custom-cart-drawer-header-right-close, .custom-cart-drawer-header-close")) {
    e.preventDefault();
    closeCartDrawer();
    return;
  }

  // Click outside box closes
  if (drawer.classList.contains("custom-cart-drawer--active")) {
    const clickedInside = e.target.closest(".custom-cart-drawer-box");
    if (!clickedInside && e.target.closest(".custom-cart-drawer")) {
      closeCartDrawer();
      return;
    }
  }

  // Quantity +/- buttons
  const qtyBtn = e.target.closest(".custom-cart-drawer-quantity-selector button");
  if (qtyBtn) {
    e.preventDefault();
    const rootItem = qtyBtn.closest("[data-line-item-key]");
    const key = rootItem?.getAttribute("data-line-item-key");
    if (!key) return;

    const input = qtyBtn.parentElement.querySelector("input");
    const currentQuantity = Number(input?.value || 1);
    const isUp = qtyBtn.classList.contains("custom-cart-drawer-quantity-selector-plus");
    const newQuantity = isUp ? currentQuantity + 1 : currentQuantity - 1;

    const res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: { [key]: newQuantity } })
    });

    const cart = await res.json();
    updateCartItemCounts(cart.item_count);
    await safeUpdateCartDrawer();
    return;
  }

  // Remove button
  const removeBtn = e.target.closest(".cart-item-remove");
  if (removeBtn) {
    e.preventDefault();
    const key = removeBtn.getAttribute("data-line-item-key");
    if (!key) return;

    const res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: { [key]: 0 } })
    });

    const cart = await res.json();
    updateCartItemCounts(cart.item_count);
    await safeUpdateCartDrawer(true);
    return;
  }
}, true);
